import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import clsx from 'clsx';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { paginationItems, FORMA_LINK_BTN } from '../../utils/listPageUi';
import localStorageService from '../../utils/localStorageService';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  PrinterIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const PaymentDetail = ({ payment }) => {
  if (!payment) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6 text-center text-slate-600 dark:text-slate-400">
        <p>Détails du paiement introuvables.</p>
      </div>
    );
  }

  const safe = (val) => val ?? '';

  // Simple function to get student name
  const getStudentName = (payment) => {
    // Direct name from payment object
    if (payment.student?.name) return payment.student.name;
    if (payment.member?.firstName && payment.member?.lastName) {
      return `${payment.member.firstName} ${payment.member.lastName}`.trim();
    }
    
    // Default names based on common payment IDs
    const nameMap = {
      'MEM001': 'Karim Benali',
      'MEM002': 'Leila Yahiaoui', 
      'MEM003': 'Omar Boucherit',
      'MEM004': 'Fatima Zerrouki',
      'MEM005': 'Nassim Hamidou',
      'ETU-2024-001': 'Amina Ben Aissa',
      'ETU-2024-002': 'Youcef Messaoudi'
    };
    
    const studentId = payment.studentId || payment.memberId;
    return nameMap[studentId] || nameMap[payment.memberId] || 'Étudiant';
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <div className="mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-1">Facture #{safe(payment.invoiceNumber || payment.reference)}</h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm">Émise le {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString('fr-FR') : '-'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <h3 className="text-sm text-slate-600 dark:text-slate-400 font-medium mb-1">Client</h3>
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-forma-500/15 flex items-center justify-center mr-3">
              <AcademicCapIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
            </div>
            <div>
              <p className="text-slate-900 dark:text-slate-50 font-medium">
                {getStudentName(payment)}
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-sm">{payment.member?.memberCode || payment.student?.code || payment.student?.memberCode || payment.student?.studentCode || 'N/A'}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <h3 className="text-sm text-slate-600 dark:text-slate-400 font-medium mb-1">Cours</h3>
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center mr-3">
              <ClipboardDocumentCheckIcon className="h-5 w-5 text-blue-300" />
            </div>
            <div>
              <p className="text-slate-900 dark:text-slate-50 font-medium">{payment.course?.title || payment.description || 'Aucun cours spécifié'}</p>
              <p className="text-slate-600 dark:text-slate-400 text-sm">{payment.course?.code || payment.category || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">Détails du paiement</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600">
            <span className="text-slate-600 dark:text-slate-400">Montant total du cours</span>
            <span className="text-slate-900 dark:text-slate-50 font-medium">{(payment.totalAmount ?? payment.amount ?? 0).toLocaleString('fr-FR')} DA</span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600">
            <span className="text-slate-600 dark:text-slate-400">Montant payé</span>
            <span className="text-green-400 font-medium">{(payment.paidAmount ?? payment.amount ?? 0).toLocaleString('fr-FR')} DA</span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600">
            <span className="text-slate-600 dark:text-slate-400">Reste à payer</span>
            <span className={`font-medium ${((payment.totalAmount ?? payment.amount ?? 0) - (payment.paidAmount ?? payment.amount ?? 0)) > 0 ? 'text-red-400' : 'text-green-400'}`}>
              {((payment.totalAmount ?? payment.amount ?? 0) - (payment.paidAmount ?? payment.amount ?? 0)).toLocaleString('fr-FR')} DA
            </span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600">
            <span className="text-slate-600 dark:text-slate-400">Méthode de paiement</span>
            <span className="text-slate-900 dark:text-slate-50 font-medium">
              {payment.method === 'Espèces' ? 'Espèces' :
               payment.method === 'Carte bancaire' ? 'Carte bancaire' :
               payment.method === 'Virement bancaire' ? 'Virement bancaire' :
               payment.method === 'Chèque' ? 'Chèque' :
               payment.paymentMethod === 'cash' ? 'Espèces' :
               payment.paymentMethod === 'card' ? 'Carte bancaire' :
               payment.paymentMethod === 'transfer' ? 'Virement' :
               payment.paymentMethod === 'check' ? 'Chèque' :
               payment.method || payment.paymentMethod || 'Non spécifié'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-600 dark:text-slate-400">Statut</span>
            <span className={`px-2 py-1 text-xs rounded-full ${payment.isPaid ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
              {payment.isPaid ? 'Payé' : 'Partiellement payé'}
            </span>
          </div>
        </div>
      </div>

      {payment.installments && payment.installments.length > 0 && (
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">Échéancier de paiement</h3>
          <div className="space-y-3">
            {payment.installments.map((installment, index) => (
              <div key={index} className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600">
                <div>
                  <span className="text-slate-600 dark:text-slate-400">Échéance {index + 1}</span>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Date: {new Date(installment.dueDate).toLocaleDateString('fr-FR')}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-900 dark:text-slate-50 font-medium">{installment.amount.toLocaleString('fr-FR')} DA</span>
                  {installment.isPaid ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-400" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PaymentForm = ({ isEditing = false, paymentData = null, students = [], courses = [] }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [paymentType, setPaymentType] = useState(paymentData?.installments ? 'partial' : 'full');
  
  const [formData, setFormData] = useState({
    studentId: paymentData?.student?.id || '',
    courseId: paymentData?.course?.id || '',
    totalAmount: paymentData?.totalAmount || '',
    paidAmount: paymentData?.paidAmount || '',
    date: paymentData?.date ? new Date(paymentData.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    method: paymentData?.method || 'Espèces',
    notes: paymentData?.notes || '',
    reference: paymentData?.reference || `FAC-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`
  });
  
  // Use real-time data from localStorage via props (interconnected with other pages)
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Listen for localStorage changes to update form data in real-time
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'students' || e.key === 'courses') {
        console.log('🔄 PaymentForm: Detected localStorage change for', e.key);
        setRefreshTrigger(prev => prev + 1);
        // Force re-render to get fresh data from props
        queryClient.invalidateQueries(['students-table']);
        queryClient.invalidateQueries(['courses-table']);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events within the same tab
    const handleCustomStorageChange = (e) => {
      if (e.detail && (e.detail.key === 'students' || e.detail.key === 'courses')) {
        console.log('🔄 PaymentForm: Detected custom storage change for', e.detail.key);
        setRefreshTrigger(prev => prev + 1);
      }
    };
    
    window.addEventListener('localStorageChange', handleCustomStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, [queryClient]);

  // Use real-time data from props (connected to other pages)
  const realCourses = courses || [];
  const realStudents = students || [];
  
  console.log('📋 PaymentForm data:', {
    studentsCount: realStudents.length,
    coursesCount: realCourses.length,
    refreshTrigger
  });

  // NEW: Get available batch enrollments for selected student
  const getAvailableBatchesForStudent = (studentId) => {
    if (!studentId) return [];
    
    try {
      // Get student's active enrollments
      const enrollments = localStorageService.getStudentEnrollments(studentId);
      const activeEnrollments = enrollments.filter(e => e.status === 'active');
      
      console.log('📋 Student enrollments for', studentId, ':', activeEnrollments);
      
      // Get batch details for each enrollment
      const studentBatches = activeEnrollments.map(enrollment => {
        const batch = localStorageService.getBatchWithEnrollment(enrollment.batchId);
        if (batch) {
          return {
            ...batch,
            enrollmentId: enrollment.id,
            enrollmentDate: enrollment.enrollmentDate,
            paymentStatus: enrollment.paymentStatus || 'pending'
          };
        }
        return null;
      }).filter(Boolean);
      
      console.log('📚 Available batches for student:', studentBatches);
      return studentBatches;
    } catch (error) {
      console.error('Error getting student batches:', error);
      return [];
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    // Auto-fill batch and amount when student is selected
    if (name === 'studentId' && value) {
      const safeStudents = Array.isArray(realStudents) ? realStudents : [];
      const selectedStudent = safeStudents.find(s => s.id === value);
      
      // Clear previous selections when student changes
      setFormData(prev => ({
        ...prev,
        [name]: value,
        batchId: '',
        enrollmentId: '',
        totalAmount: 0,
        paidAmount: 0
      }));
      
      const studentBatches = getAvailableBatchesForStudent(value);
      if (studentBatches.length > 0) {
        toast.success(`✅ ${studentBatches.length} inscription(s) trouvée(s) pour ${selectedStudent?.name}`, {
          duration: 2000,
          position: 'top-right'
        });
      } else {
        toast.info(`ℹ️ Aucune inscription active pour ${selectedStudent?.name}`, {
          duration: 2000,
          position: 'top-right'
        });
      }
    }
    // Auto-fill total amount when batch enrollment is selected
    else if (name === 'batchId' && value) {
      const studentBatches = getAvailableBatchesForStudent(formData.studentId);
      const selectedBatch = studentBatches.find(batch => batch.id === value);
      
      if (selectedBatch) {
        // Get course details to find price
        const safeCourses = Array.isArray(realCourses) ? realCourses : [];
        const course = safeCourses.find(c => c.id === selectedBatch.courseId);
        const coursePrice = parseFloat(course?.price) || 0;
        
        setFormData(prev => ({
          ...prev,
          [name]: value,
          enrollmentId: selectedBatch.enrollmentId,
          totalAmount: coursePrice,
          paidAmount: paymentType === 'full' ? coursePrice : prev.paidAmount
        }));
        
        toast.success(`✅ Groupe "${selectedBatch.batchName}" sélectionné - Prix: ${coursePrice.toLocaleString()} DA`, {
          duration: 2000,
          position: 'top-right'
        });
      }
    } else {
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate required fields
      if (!formData.studentId) {
        toast.error('Veuillez sélectionner un étudiant');
        return;
      }
      
      if (!formData.batchId) {
        toast.error('Veuillez sélectionner une inscription (groupe)');
        return;
      }
      
      if (!formData.totalAmount) {
        toast.error('Le montant total est requis');
        return;
      }
      
      // Prepare payment data
      let paymentToSave = {
        ...formData,
        // For full payment, set paidAmount to totalAmount and mark as paid
        paidAmount: paymentType === 'full' ? formData.totalAmount : formData.paidAmount,
        isPaid: paymentType === 'full' || parseFloat(formData.totalAmount) === parseFloat(formData.paidAmount),
        remainingAmount: paymentType === 'partial' ? parseFloat(formData.totalAmount) - parseFloat(formData.paidAmount) : 0
      };
      
      // Look up actual student and batch info from real data
      console.log('Looking for student ID:', formData.studentId);
      console.log('Looking for batch ID:', formData.batchId);
      console.log('Looking for enrollment ID:', formData.enrollmentId);
      
      const selectedStudent = realStudents.find(s => s.id === formData.studentId);
      const studentBatches = getAvailableBatchesForStudent(formData.studentId);
      const selectedBatch = studentBatches.find(b => b.id === formData.batchId);
      const selectedCourse = selectedBatch ? realCourses.find(c => c.id === selectedBatch.courseId) : null;
      
      console.log('Found student:', selectedStudent);
      console.log('Found batch:', selectedBatch);
      console.log('Found course:', selectedCourse);
      
      // Add batch and enrollment information to payment
      if (selectedBatch) {
        paymentToSave.batch = {
          id: selectedBatch.id,
          name: selectedBatch.batchName,
          courseName: selectedBatch.courseName,
          days: selectedBatch.days,
          startTime: selectedBatch.startTime,
          endTime: selectedBatch.endTime,
          trainerName: selectedBatch.trainerName
        };
        paymentToSave.batchId = selectedBatch.id;
        paymentToSave.enrollmentId = formData.enrollmentId;
      }
      
      // Always assign student info (using member data structure)
      if (selectedStudent) {
        const studentName = selectedStudent.name || 
                           `${selectedStudent.firstName || ''} ${selectedStudent.lastName || ''}`.trim() ||
                           selectedStudent.fullName ||
                           `Étudiant-${selectedStudent.id}`;
        
        paymentToSave.student = {
          id: selectedStudent.id,
          name: studentName,
          firstName: selectedStudent.firstName,
          lastName: selectedStudent.lastName,
          code: selectedStudent.memberCode || selectedStudent.code || selectedStudent.studentCode || 'N/A'
        };
        // Also store member reference for consistency
        paymentToSave.member = selectedStudent;
        paymentToSave.studentId = selectedStudent.id;
        console.log('Assigned student to payment:', paymentToSave.student);
      } else {
        console.log('No student found for ID:', formData.studentId);
        paymentToSave.student = { 
          id: formData.studentId, 
          name: 'Étudiant introuvable',
          firstName: 'Étudiant',
          lastName: 'Introuvable',
          code: 'N/A' 
        };
        paymentToSave.studentId = formData.studentId;
      }
      
      // Always assign course info using real course data structure
      if (selectedCourse) {
        paymentToSave.course = {
          id: selectedCourse.id,
          title: selectedCourse.title || selectedCourse.name,
          code: selectedCourse.code || selectedCourse.id
        };
        console.log('Assigned course to payment:', paymentToSave.course);
      } else {
        console.log('No course found for ID:', formData.courseId);
        paymentToSave.course = { id: formData.courseId, title: 'Course Not Found', code: 'N/A' };
      }
      
      console.log('Final payment object before saving:', paymentToSave);
      
      // Validate partial payment amount
      if (paymentType === 'partial') {
        const totalAmount = parseFloat(formData.totalAmount);
        const paidAmount = parseFloat(formData.paidAmount);
        
        if (paidAmount > totalAmount) {
          alert('Le montant payé ne peut pas être supérieur au montant total');
          return;
        }
        
        if (paidAmount <= 0) {
          alert('Le montant payé doit être supérieur à 0');
          return;
        }
      }
      
      if (isEditing && paymentData) {
        // Update existing payment
        const updatedPayment = {
          ...paymentData,
          ...paymentToSave
        };
        const result = localStorageService.updateItem('payments', updatedPayment);
        console.log('Payment updated successfully:', result);
      } else {
        // Add new payment
        const newPayment = localStorageService.addItem('payments', paymentToSave);
        console.log('Payment added successfully:', newPayment);
      }
      
      // Invalidate React Query cache to refresh all components using payments data
      await queryClient.invalidateQueries('payments');
      
      navigate('/payments');
    } catch (error) {
      console.error('Error saving payment data:', error);
      toast.error('Une erreur est survenue lors de l\'enregistrement des données');
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
        {isEditing ? 'Modifier le paiement' : 'Nouveau paiement'}
      </h2>
      
      {/* Real-time data connection indicator */}
      {(realStudents.length === 0 || realCourses.length === 0) && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
          <div className="flex items-center text-yellow-300">
            <ArrowPathIcon className="w-5 h-5 mr-2" />
            {realStudents.length === 0 && realCourses.length === 0 
              ? "Aucun étudiant ni cours trouvé. Ajoutez-les dans leurs pages respectives."
              : realStudents.length === 0 
                ? "Aucun étudiant trouvé. Ajoutez des étudiants dans la page Étudiants."
                : "Aucun cours trouvé. Ajoutez des cours dans la page Cours."
            }
          </div>
        </div>
      )}
      
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Étudiant
            </label>
            <select 
              name="studentId"
              value={formData.studentId}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
              disabled={realStudents.length === 0}
            >
              <option value="">
                {realStudents.length === 0 ? "Aucun étudiant disponible" : "Sélectionner un étudiant"}
              </option>
              {(Array.isArray(realStudents) ? realStudents : []).map(student => (
                <option key={student.id} value={student.id}>
                  {student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim()} 
                  {student.currentCourse && ` • ${typeof student.currentCourse === 'string' ? 'Cours assigné' : student.currentCourse.title}`}
                  ({student.memberCode || student.code || student.id})
                </option>
              ))}
            </select>
            {realStudents.length > 0 && (
              <div className="text-green-400 text-xs mt-1 flex items-center">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                {realStudents.length} étudiant{realStudents.length > 1 ? 's' : ''} disponible{realStudents.length > 1 ? 's' : ''}
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Inscription (Groupe)
            </label>
            <select 
              name="batchId"
              value={formData.batchId || ''}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
              disabled={!formData.studentId}
            >
              <option value="">
                {!formData.studentId ? "Sélectionnez d'abord un étudiant" : "Sélectionner un groupe"}
              </option>
              {formData.studentId && getAvailableBatchesForStudent(formData.studentId).map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.courseName} - {batch.batchName} 
                  ({batch.days.join(', ')} • {batch.startTime}-{batch.endTime})
                  {batch.paymentStatus === 'paid' ? ' ✅ Payé' : batch.paymentStatus === 'partial' ? ' 🟡 Partiel' : ' ⏳ En attente'}
                </option>
              ))}
            </select>
            {formData.studentId && (
              <div className="text-blue-400 text-xs mt-1 flex items-center">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                {getAvailableBatchesForStudent(formData.studentId).length} inscription(s) active(s)
              </div>
            )}
            {!formData.studentId && (
              <div className="text-orange-400 text-xs mt-1">
                Veuillez d'abord sélectionner un étudiant pour voir ses inscriptions
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Montant total (DA)
            </label>
                          <input 
                type="number" 
                name="totalAmount"
                value={formData.totalAmount}
                readOnly
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-600 dark:text-slate-400 cursor-not-allowed"
                placeholder="Sélectionnez un cours pour voir le prix"
              />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Date
            </label>
                          <input 
                type="date" 
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                required
              />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Type de paiement
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="paymentType"
                  value="full"
                  checked={paymentType === 'full'}
                  onChange={() => {
                    setPaymentType('full');
                    // For full payment, paidAmount equals totalAmount (no separate input needed)
                    setFormData(prev => ({ ...prev, paidAmount: prev.totalAmount }));
                  }}
                  className="text-forma-600 focus:ring-forma-500"
                />
                <span className="text-slate-900 dark:text-slate-50">Paiement complet</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="paymentType"
                  value="partial"
                  checked={paymentType === 'partial'}
                  onChange={() => {
                    setPaymentType('partial');
                    // Reset paidAmount to 0 for partial payment
                    setFormData(prev => ({ ...prev, paidAmount: 0 }));
                  }}
                  className="text-forma-600 focus:ring-forma-500"
                />
                <span className="text-slate-900 dark:text-slate-50">Paiement partiel</span>
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Méthode de paiement
            </label>
            <select 
              name="method"
              value={formData.method}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            >
              <option value="Espèces">Espèces</option>
              <option value="Carte bancaire">Carte bancaire</option>
              <option value="Virement bancaire">Virement bancaire</option>
              <option value="Chèque">Chèque</option>
            </select>
          </div>
          
          {paymentType === 'partial' && (
            <div className="md:col-span-2">
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Montant payé aujourd'hui (DA)
              </label>
                              <input 
                type="number" 
                name="paidAmount"
                value={formData.paidAmount}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                required
                min="0"
                max={formData.totalAmount}
                placeholder="Entrez le montant payé"
              />
              {formData.totalAmount && formData.paidAmount && (
                <div className="mt-2 p-3 bg-slate-100/80 dark:bg-slate-800/40 rounded-lg border border-slate-200 dark:border-slate-600">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Montant total:</span>
                    <span className="text-slate-900 dark:text-slate-50 font-medium">{parseFloat(formData.totalAmount).toLocaleString('fr-FR')} DA</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-1">
                    <span className="text-slate-600 dark:text-slate-400">Montant payé:</span>
                    <span className="text-green-400 font-medium">{parseFloat(formData.paidAmount).toLocaleString('fr-FR')} DA</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-1 pt-2 border-t border-slate-200 dark:border-slate-600">
                    <span className="text-slate-600 dark:text-slate-400">Montant restant:</span>
                    <span className="text-red-400 font-medium">{(parseFloat(formData.totalAmount) - parseFloat(formData.paidAmount)).toLocaleString('fr-FR')} DA</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Remarques
            </label>
                          <textarea 
              rows="3"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              placeholder="Informations supplémentaires sur ce paiement..."
            ></textarea>
          </div>
        </div>
        
        <div className="flex justify-end space-x-4">
          <Link 
            to="/payments"
            className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            Annuler
          </Link>
          <button
            type="submit"
            className="px-4 py-2 rounded-lg font-medium text-white shadow bg-forma-600 hover:bg-forma-500 transition-all duration-200"
          >
            {isEditing ? "Mettre à jour" : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
};

// Component for payment detail route that uses useParams
const PaymentDetailRoute = ({ payments }) => {
  const { id } = useParams();
  const payment = payments.find(p => p.id === id);
  
  console.log('PaymentDetailRoute: Looking for payment with ID:', id);
  console.log('PaymentDetailRoute: Available payments:', payments.map(p => ({ id: p.id, ref: p.reference })));
  console.log('PaymentDetailRoute: Found payment:', payment);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mb-6">
        <Link to="/payments" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Retour aux paiements
        </Link>
      </div>
      <PaymentDetail payment={payment} />
    </motion.div>
  );
};

// Component for payment edit route that uses useParams
const PaymentEditRoute = ({ payments, students, courses }) => {
  const { id } = useParams();
  const payment = payments.find(p => p.id === id);
  
  console.log('PaymentEditRoute: Looking for payment with ID:', id);
  console.log('PaymentEditRoute: Found payment:', payment);
  
  return (
    <PaymentForm 
      isEditing={true} 
      paymentData={payment}
      students={students}
      courses={courses}
    />
  );
};

const PaymentsPage = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');

  // Remove local state - using React Query as single source of truth
  
  // Multi-select state
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  
  // Real-time data interconnection - Listen for changes in other pages
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'students' || e.key === 'courses') {
        console.log('🔄 PaymentsPage: Detected localStorage change for', e.key);
        queryClient.invalidateQueries(['students-table']);
        queryClient.invalidateQueries(['courses-table']);
      }
    };

    // Listen for cross-tab changes
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for same-tab changes via custom events
    const handleCustomStorageChange = (e) => {
      if (e.detail && (e.detail.key === 'students' || e.detail.key === 'courses')) {
        console.log('🔄 PaymentsPage: Detected custom storage change for', e.detail.key);
        queryClient.invalidateQueries(['students-table']);
        queryClient.invalidateQueries(['courses-table']);
      }
    };
    
    window.addEventListener('localStorageChange', handleCustomStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, [queryClient]);
  
  // Multi-select functions will be defined after paginatedPayments calculation
  
  
  // Fetch payments using React Query from localStorage (single source of truth)
  const { data: paymentsData, refetch: refetchPayments } = useQuery(
    'payments',
    () => {
      try {
        const payments = localStorageService.getAllItems('payments');
        console.log(`✅ Loaded ${payments.length} payments from localStorage`);
        return { payments };
    } catch (error) {
        console.error('Error fetching payments:', error);
        return { payments: [] };
      }
    },
    {
      staleTime: 0, // Always fetch fresh data
      cacheTime: 1000, // Short cache to prevent stale data
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );

  // Fetch students for real-time interconnected data
  const { data: studentsData } = useQuery(
    'students-table',
    () => {
    try {
        const students = localStorageService.getAllItems('students');
        const members = localStorageService.getAllItems('members');
        console.log('🔄 PaymentsPage: Fetched students/members:', { students: students.length, members: members.length });
        return [...students, ...members];
    } catch (error) {
        console.error('Error fetching students:', error);
        return [];
      }
    },
    {
      staleTime: 0, // Always fetch fresh data for real-time updates
      cacheTime: 1000, // Short cache
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );

  // Fetch courses for real-time interconnected data  
  const { data: coursesData } = useQuery(
    'courses-table',
    () => {
      try {
        const courses = localStorageService.getAllItems('courses');
        console.log('🔄 PaymentsPage: Fetched courses:', courses.length);
        return courses;
      } catch (error) {
        console.error('Error fetching courses:', error);
        return [];
      }
    },
    {
      staleTime: 0, // Always fetch fresh data for real-time updates
      cacheTime: 1000, // Short cache
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );

  // Extract data from React Query
  const payments = useMemo(() => paymentsData?.payments ?? [], [paymentsData?.payments]);
  const students = studentsData || [];
  const courses = coursesData || [];
  
  // One-time refresh of localStorage data if needed
  React.useEffect(() => {
    const hasRefreshed = localStorage.getItem('studentsRefreshed');
    if (!hasRefreshed) {
      localStorage.removeItem('students');
      localStorage.setItem('studentsRefreshed', 'true');
      // Force component re-render by refreshing the data
      setTimeout(() => window.location.reload(), 100);
    }
  }, []);
  
  // Simple function to get student name for any payment
  const getStudentName = (payment) => {
    // Direct name from payment object
    if (payment.student?.name) return payment.student.name;
    if (payment.member?.firstName && payment.member?.lastName) {
      return `${payment.member.firstName} ${payment.member.lastName}`.trim();
    }
    
    // Find student by ID
    const studentId = payment.studentId || payment.memberId;
    if (studentId && students.length > 0) {
      const student = students.find(s => 
        s.id === studentId || 
        s.memberCode === studentId || 
        s.studentCode === studentId
      );
      if (student) {
        return student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim();
      }
    }
    
    // Default names based on common payment IDs
    const nameMap = {
      'MEM001': 'Karim Benali',
      'MEM002': 'Leila Yahiaoui', 
      'MEM003': 'Omar Boucherit',
      'MEM004': 'Fatima Zerrouki',
      'MEM005': 'Nassim Hamidou',
      'ETU-2024-001': 'Amina Ben Aissa',
      'ETU-2024-002': 'Youcef Messaoudi'
    };
    
    return nameMap[studentId] || nameMap[payment.memberId] || 'Étudiant';
  };

  // Fetch organization settings including logo from localStorage
  const { data: organizationInfo, isLoading: settingsLoading } = useQuery('settings', async () => {
    try {
    const response = await axios.get('/api/settings');
      const apiData = response.data;
      
      // Get logo from localStorage if not in API response
      const localSettings = localStorage.getItem('organizationSettings');
      const localData = localSettings ? JSON.parse(localSettings) : {};
      
      // Merge API data with local data (logo)
      return {
        ...apiData,
        ...localData,
        // API data takes precedence for everything except logo
        logo: localData.logo || apiData.logo
      };
    } catch (error) {
      console.warn('API settings not available, using localStorage:', error);
      // Fallback to localStorage only
      const localSettings = localStorage.getItem('organizationSettings');
      return localSettings ? JSON.parse(localSettings) : {
        gymName: 'FORMAPRO',
        address: 'École de Formation',
        phone: 'Téléphone non renseigné'
      };
    }
  });
  
  // React Query handles data refreshing automatically
  
  // React Query handles all data loading automatically

  // Filtrage des paiements
  const filteredPayments = payments.filter(payment => {
    // Recherche par texte
    const matchesSearch = 
      (payment.reference ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (payment.student?.name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (payment.course?.title ?? '').toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Filtrage par statut
    if (activeTab === 'all') return true;
    if (activeTab === 'paid') return payment.isPaid;
    if (activeTab === 'partial') return !payment.isPaid;
    
    return true;
  });
  
  const paginatedPayments = filteredPayments.slice((currentPage - 1) * paymentsPerPage, currentPage * paymentsPerPage);
  const totalPayments = filteredPayments.length;
  const totalPages = Math.max(1, Math.ceil(totalPayments / paymentsPerPage));

  const formaLinkBtn = FORMA_LINK_BTN;

  const statsStrip = useMemo(() => {
    const paidTotal = payments.reduce((acc, payment) => {
      const paidAmount = parseFloat(payment.paidAmount) || parseFloat(payment.amount) || 0;
      return acc + paidAmount;
    }, 0);
    const outstandingTotal = payments.reduce((acc, payment) => {
      const totalAmount = parseFloat(payment.totalAmount) || parseFloat(payment.amount) || 0;
      const paidAmount = parseFloat(payment.paidAmount) || parseFloat(payment.amount) || 0;
      return acc + Math.max(0, totalAmount - paidAmount);
    }, 0);
    return [
      {
        title: 'Total paiements',
        value: String(payments.length),
        hint: 'Enregistrements',
        icon: DocumentTextIcon,
        iconWrap: 'bg-forma-500/15 text-forma-700 dark:bg-forma-500/20 dark:text-forma-200',
      },
      {
        title: 'Montant payé',
        value: `${paidTotal.toLocaleString('fr-FR')} DA`,
        hint: 'Encaissements cumulés',
        icon: CurrencyDollarIcon,
        iconWrap: 'bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-100',
      },
      {
        title: 'Complètement payés',
        value: String(payments.filter((p) => p.isPaid).length),
        hint: 'Factures soldées',
        icon: CheckCircleIcon,
        iconWrap: 'bg-teal-500/15 text-teal-900 dark:bg-teal-500/20 dark:text-teal-100',
      },
      {
        title: 'Reste à encaisser',
        value: `${outstandingTotal.toLocaleString('fr-FR')} DA`,
        hint: 'Montants ouverts',
        icon: ClockIcon,
        iconWrap: 'bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100',
      },
    ];
  }, [payments]);

  const pageNav = paginationItems(currentPage, totalPages);
  const pageSliceLabel =
    totalPayments === 0
      ? 'Aucun paiement à afficher'
      : `Affichage ${Math.min((currentPage - 1) * paymentsPerPage + 1, totalPayments)}–${Math.min(currentPage * paymentsPerPage, totalPayments)} sur ${totalPayments}`;

  // Multi-select functions
  const handleSelectPayment = (paymentId) => {
    setSelectedPayments(prev => {
      if (prev.includes(paymentId)) {
        // Remove from selection
        const newSelection = prev.filter(id => id !== paymentId);
        setIsSelectAll(false);
        return newSelection;
      } else {
        // Add to selection
        const newSelection = [...prev, paymentId];
        // Check if all visible payments are now selected
        setIsSelectAll(newSelection.length === paginatedPayments.length);
        return newSelection;
      }
    });
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      // Deselect all
      setSelectedPayments([]);
      setIsSelectAll(false);
    } else {
      // Select all visible payments
      const allPaymentIds = paginatedPayments.map(payment => payment.id);
      setSelectedPayments(allPaymentIds);
      setIsSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Delete selected payments from localStorage
      selectedPayments.forEach(paymentId => {
        localStorageService.deleteItem('payments', paymentId);
      });
      
      // Invalidate React Query cache to refetch fresh data
      await queryClient.invalidateQueries('payments');
      
      // Clear selection
      setSelectedPayments([]);
      setIsSelectAll(false);
      setShowBulkDeleteModal(false);
      
      toast.success(`${selectedPayments.length} paiement(s) supprimé(s) avec succès`);
    } catch (error) {
      console.error('Error deleting payments:', error);
      toast.error('Erreur lors de la suppression des paiements');
    }
  };

  // Update select all state when pagination changes
  React.useEffect(() => {
    if (selectedPayments.length === 0) {
      setIsSelectAll(false);
    } else {
      const visiblePaymentIds = paginatedPayments.map(p => p.id);
      const allVisibleSelected = visiblePaymentIds.every(id => selectedPayments.includes(id));
      setIsSelectAll(allVisibleSelected && visiblePaymentIds.length > 0);
    }
  }, [selectedPayments, paginatedPayments]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const handleDelete = async (paymentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce paiement ?')) {
      try {
        // Supprimer de localStorage
        const success = localStorageService.deleteItem('payments', paymentId);
        console.log('Delete payment operation result:', success);
        
        if (success) {
          // Invalidate React Query cache to refetch fresh data
          await queryClient.invalidateQueries('payments');
          toast.success('Paiement supprimé avec succès');
        } else {
          toast.error('Erreur lors de la suppression du paiement');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Une erreur est survenue lors de la suppression');
      }
    }
  };

  // Helper function to find member for a payment
  const findMemberForPayment = (payment) => {
    let member = null;
    
    // Try to find member from API data first
    if (payment.member) {
      member = payment.member;
    } else if (payment.memberId && students.length > 0) {
      member = students.find(m => m.id === payment.memberId);
    } 
    // Try to find student by ID in students data
    else if (payment.studentId) {
      const student = students.find(s => s.id === payment.studentId);
      if (student) {
      member = {
          firstName: student.firstName || student.name?.split(' ')[0] || '',
          lastName: student.lastName || student.name?.split(' ').slice(1).join(' ') || '',
          memberCode: student.memberCode || student.code || student.studentCode || 'N/A'
        };
      }
    }
    // Fallback to stored student data
    else if (payment.student) {
      member = {
        firstName: payment.student.firstName || payment.student.name?.split(' ')[0] || '',
        lastName: payment.student.lastName || payment.student.name?.split(' ').slice(1).join(' ') || '',
        memberCode: payment.student.code || payment.student.memberCode || payment.student.studentCode || 'N/A'
      };
    }
    
    return member;
  };

  // Helper function to get payment method label
  const getPaymentMethodLabel = (method) => {
    const methods = {
      'cash': 'Espèces',
      'card': 'Carte bancaire',
      'transfer': 'Virement',
      'check': 'Chèque',
      'undefined': 'Non défini'
    };
    return methods[method] || method || 'Non défini';
  };

  // Helper function to convert number to French words
  const numberToWords = (num) => {
    if (num === 0) return 'zéro';
    
    const ones = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
    const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
    const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];
    const hundreds = 'cent';
    const thousands = 'mille';
    
    function convertHundreds(n) {
      let result = '';
      
      if (n >= 100) {
        const h = Math.floor(n / 100);
        if (h === 1) {
          result += hundreds;
    } else {
          result += ones[h] + ' ' + hundreds;
        }
        n %= 100;
        if (n > 0) result += ' ';
      }
      
      if (n >= 20) {
        const t = Math.floor(n / 10);
        result += tens[t];
        n %= 10;
        if (n > 0) {
          if (t === 8) result += '-' + ones[n];
          else result += '-' + ones[n];
        }
      } else if (n >= 10) {
        result += teens[n - 10];
      } else if (n > 0) {
        result += ones[n];
      }
      
      return result;
    }
    
    if (num >= 1000) {
      const t = Math.floor(num / 1000);
      let result = '';
      if (t === 1) {
        result += thousands;
      } else {
        result += convertHundreds(t) + ' ' + thousands;
      }
      num %= 1000;
      if (num > 0) {
        result += ' ' + convertHundreds(num);
      }
      return result;
    } else {
      return convertHundreds(num);
    }
  };

  // Direct PDF Export function
  const handleDirectPDFExport = async (payment) => {
    try {
      // Check if settings are still loading
      if (settingsLoading) {
        toast.error('Chargement en cours, veuillez patienter...');
        return;
      }

      const member = findMemberForPayment(payment);
      if (!member) {
      toast.error('Impossible de trouver les informations du membre pour ce paiement');
        return;
      }

      console.log('Organization info:', organizationInfo);

      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      
      // Create A6 landscape receipt (148mm x 105mm)
      const doc = new jsPDF('l', 'mm', [105, 148]);
      const pageWidth = doc.internal.pageSize.width;
      
      // Set font
      doc.setFont('helvetica');
      
      // Organization info with safe fallbacks
      const orgInfo = {
        name: organizationInfo?.gymName || organizationInfo?.name || 'FORMAPRO',
        address: organizationInfo?.address || 'École de Formation',
        phone: organizationInfo?.phone || 'Téléphone non renseigné',
        logo: organizationInfo?.logo || null
      };
      
      // Receipt data
      const receiptNumber = payment.receiptNumber || `REC-${Math.floor(Math.random() * 1000000)}`;
      const paymentDate = payment.date ? new Date(payment.date).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR');
      const paymentAmount = payment.amount || payment.paidAmount || payment.totalAmount || 0;
      const amountInWords = numberToWords(paymentAmount);
      
      // Add logo if available
      let logoHeight = 0;
      if (orgInfo.logo) {
        try {
          // Determine image format from data URL
          let format = 'JPEG';
          if (orgInfo.logo.includes('data:image/png')) {
            format = 'PNG';
          } else if (orgInfo.logo.includes('data:image/jpeg') || orgInfo.logo.includes('data:image/jpg')) {
            format = 'JPEG';
          }
          
          doc.addImage(orgInfo.logo, format, 10, 8, 20, 20);
          logoHeight = 25;
        } catch (error) {
          console.warn('Could not add logo to PDF, continuing without logo:', error);
          // Continue without logo if it fails
        }
      }

      // Header for A6 landscape (adjust position based on logo)
      const headerY = logoHeight > 0 ? 15 : 15;
      const headerX = logoHeight > 0 ? 35 : 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(orgInfo.name, headerX, headerY);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(orgInfo.address, headerX, headerY + 5);
      doc.text(orgInfo.phone, headerX, headerY + 9);
      
      // Receipt number and date (top right)
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`N°: ${receiptNumber}`, pageWidth - 50, 15);
      doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${paymentDate}`, pageWidth - 50, 19);
      
      // Separator line
      doc.setLineWidth(0.5);
      doc.line(10, 28, pageWidth - 10, 28);
      
      // Receipt title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('REÇU', pageWidth / 2, 40, { align: 'center' });
      
      // Content in two columns
      const startY = 52;
      const lineHeight = 6;
      const leftColumn = 10;
      const rightColumn = pageWidth / 2 + 10;
      
      // Left column
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`Reçu de:`, leftColumn, startY);
      doc.setFont('helvetica', 'normal');
      doc.text(`${member?.firstName || ''} ${member?.lastName || ''}`, leftColumn, startY + lineHeight);
      
      doc.setFont('helvetica', 'bold');
      doc.text(`Pour:`, leftColumn, startY + lineHeight * 3);
      doc.setFont('helvetica', 'normal');
      doc.text(`${payment.description || 'Paiement de formation'}`, leftColumn + 12, startY + lineHeight * 3, { maxWidth: 42 });
      
      doc.setFont('helvetica', 'bold');
      doc.text(`Méthode:`, leftColumn, startY + lineHeight * 5);
      doc.setFont('helvetica', 'normal');
      doc.text(`${getPaymentMethodLabel(payment.paymentMethod)}`, leftColumn + 20, startY + lineHeight * 5);
      
      if (payment.reference) {
        doc.setFont('helvetica', 'bold');
        doc.text(`Référence:`, leftColumn, startY + lineHeight * 7);
        doc.setFont('helvetica', 'normal');
        doc.text(`${payment.reference}`, leftColumn + 25, startY + lineHeight * 7);
      }
      
      // Right column
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Montant: ${paymentAmount.toLocaleString()} DA`, rightColumn, startY);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`En lettres:`, rightColumn, startY + lineHeight * 2);
      doc.setFont('helvetica', 'normal');
      doc.text(`${amountInWords} dinars`, rightColumn, startY + lineHeight * 3, { maxWidth: 45 });
      
      
      // Save PDF
      doc.save(`Recu_Paiement_${receiptNumber}.pdf`);
      toast.success('PDF exporté avec succès');
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  // Direct Print function
  const handleDirectPrint = async (payment) => {
    try {
      // Check if settings are still loading
      if (settingsLoading) {
        toast.error('Chargement en cours, veuillez patienter...');
        return;
      }

      const member = findMemberForPayment(payment);
      if (!member) {
        toast.error('Impossible de trouver les informations du membre pour ce paiement');
        return;
      }

      console.log('Organization info for print:', organizationInfo);

      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      
      // Create A6 landscape receipt (148mm x 105mm) - identical to export
      const doc = new jsPDF('l', 'mm', [105, 148]);
      const pageWidth = doc.internal.pageSize.width;
      
      // Set font
      doc.setFont('helvetica');
      
      // Organization info with safe fallbacks
      const orgInfo = {
        name: organizationInfo?.gymName || organizationInfo?.name || 'FORMAPRO',
        address: organizationInfo?.address || 'École de Formation',
        phone: organizationInfo?.phone || 'Téléphone non renseigné',
        logo: organizationInfo?.logo || null
      };
      
      // Receipt data
      const receiptNumber = payment.receiptNumber || `REC-${Math.floor(Math.random() * 1000000)}`;
      const paymentDate = payment.date ? new Date(payment.date).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR');
      const paymentAmount = payment.amount || payment.paidAmount || payment.totalAmount || 0;
      const amountInWords = numberToWords(paymentAmount);
      
      // Add logo if available
      let logoHeight = 0;
      if (orgInfo.logo) {
        try {
          // Determine image format from data URL
          let format = 'JPEG';
          if (orgInfo.logo.includes('data:image/png')) {
            format = 'PNG';
          } else if (orgInfo.logo.includes('data:image/jpeg') || orgInfo.logo.includes('data:image/jpg')) {
            format = 'JPEG';
          }
          
          doc.addImage(orgInfo.logo, format, 10, 8, 20, 20);
          logoHeight = 25;
        } catch (error) {
          console.warn('Could not add logo to PDF, continuing without logo:', error);
          // Continue without logo if it fails
        }
      }

      // Header for A6 landscape - exact match with export
      const headerY = logoHeight > 0 ? 15 : 15;
      const headerX = logoHeight > 0 ? 35 : 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(orgInfo.name, headerX, headerY);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(orgInfo.address, headerX, headerY + 5);
      doc.text(orgInfo.phone, headerX, headerY + 9);
      
      // Receipt number and date (top right)
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`N°: ${receiptNumber}`, pageWidth - 50, 15);
      doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${paymentDate}`, pageWidth - 50, 19);
      
      // Separator line
      doc.setLineWidth(0.5);
      doc.line(10, 28, pageWidth - 10, 28);
      
      // Receipt title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('REÇU', pageWidth / 2, 40, { align: 'center' });
      
      // Content in two columns - exact positioning
      const startY = 52;
      const lineHeight = 6;
      const leftColumn = 10;
      const rightColumn = pageWidth / 2 + 10;
      
      // Left column - exact match with export
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`Reçu de:`, leftColumn, startY);
      doc.setFont('helvetica', 'normal');
      doc.text(`${member?.firstName || ''} ${member?.lastName || ''}`, leftColumn, startY + lineHeight);
      
      doc.setFont('helvetica', 'bold');
      doc.text(`Pour:`, leftColumn, startY + lineHeight * 3);
      doc.setFont('helvetica', 'normal');
      doc.text(`${payment.description || 'Paiement de formation'}`, leftColumn + 12, startY + lineHeight * 3, { maxWidth: 42 });
      
      doc.setFont('helvetica', 'bold');
      doc.text(`Méthode:`, leftColumn, startY + lineHeight * 5);
      doc.setFont('helvetica', 'normal');
      doc.text(`${getPaymentMethodLabel(payment.paymentMethod)}`, leftColumn + 20, startY + lineHeight * 5);
      
      if (payment.reference) {
        doc.setFont('helvetica', 'bold');
        doc.text(`Référence:`, leftColumn, startY + lineHeight * 7);
        doc.setFont('helvetica', 'normal');
        doc.text(`${payment.reference}`, leftColumn + 25, startY + lineHeight * 7);
      }
      
      // Right column - exact match with export
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Montant: ${paymentAmount.toLocaleString()} DA`, rightColumn, startY);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`En lettres:`, rightColumn, startY + lineHeight * 2);
      doc.setFont('helvetica', 'normal');
      doc.text(`${amountInWords} dinars`, rightColumn, startY + lineHeight * 3, { maxWidth: 45 });
      
      
      // Create PDF blob and print it
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Create iframe for printing
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.top = '0';
      printFrame.style.left = '0';
      printFrame.style.width = '100%';
      printFrame.style.height = '100%';
      printFrame.style.zIndex = '9999';
      printFrame.style.backgroundColor = 'white';
      printFrame.src = pdfUrl;

      const printStyles = document.createElement('style');
      printStyles.id = 'pdf-print-styles';
      printStyles.innerHTML = `
        @media print {
          body * { visibility: hidden !important; }
          #pdf-print-frame {
            visibility: visible !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            z-index: 9999 !important;
          }
        }
      `;
      printFrame.id = 'pdf-print-frame';
      
      document.head.appendChild(printStyles);
      document.body.appendChild(printFrame);
      
      // Wait for PDF to load, then show print dialog
      printFrame.onload = () => {
        setTimeout(() => {
          try {
            // Try to print the iframe content
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
          } catch (error) {
            // Fallback: use window.print() which will print the iframe due to our CSS
            window.print();
          }
          
          // Cleanup after printing
          const cleanup = () => {
            if (document.body.contains(printFrame)) {
              document.body.removeChild(printFrame);
            }
            const existingStyles = document.getElementById('pdf-print-styles');
            if (existingStyles) {
              document.head.removeChild(existingStyles);
            }
            URL.revokeObjectURL(pdfUrl);
          };
          
          // Listen for after print to cleanup
          const afterPrint = () => {
            cleanup();
            const closeButton = document.getElementById('close-print-btn');
            if (closeButton && document.body.contains(closeButton)) {
              document.body.removeChild(closeButton);
            }
            window.removeEventListener('afterprint', afterPrint);
          };
          
          window.addEventListener('afterprint', afterPrint);
          
          // Add close button to iframe
          const closeButton = document.createElement('button');
          closeButton.id = 'close-print-btn';
          closeButton.innerHTML = '✕ Fermer';
          closeButton.style.position = 'fixed';
          closeButton.style.top = '20px';
          closeButton.style.right = '20px';
          closeButton.style.zIndex = '10000';
          closeButton.style.padding = '10px 15px';
          closeButton.style.backgroundColor = '#ff4444';
          closeButton.style.color = 'white';
          closeButton.style.border = 'none';
          closeButton.style.borderRadius = '5px';
          closeButton.style.cursor = 'pointer';
          closeButton.style.fontSize = '14px';
          closeButton.onclick = () => {
            cleanup();
            if (document.body.contains(closeButton)) {
              document.body.removeChild(closeButton);
            }
          };
          
          document.body.appendChild(closeButton);
          
          // Extended fallback cleanup (30 seconds)
          setTimeout(() => {
            if (document.body.contains(printFrame)) {
              cleanup();
              if (document.body.contains(closeButton)) {
                document.body.removeChild(closeButton);
              }
            }
          }, 30000);
        }, 1000);
      };
      
      // Handle iframe load error
      printFrame.onerror = () => {
        console.error('Failed to load PDF in iframe');
        if (document.body.contains(printFrame)) {
          document.body.removeChild(printFrame);
        }
        const existingStyles = document.getElementById('pdf-print-styles');
        if (existingStyles) {
          document.head.removeChild(existingStyles);
        }
        URL.revokeObjectURL(pdfUrl);
        toast.error('Erreur lors du chargement de l\'impression');
      };
      
    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      toast.error('Erreur lors de l\'impression');
    }
  };

  return (
    <div>
      <Routes>
      <Route
        path="/"
        element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <PageHeader
              title="Paiements"
              description="Gestion des paiements et transactions"
              actions={
                <Link to="/payments/add" className={formaLinkBtn}>
                  <PlusIcon className="h-5 w-5" />
                  <span>Nouveau paiement</span>
                </Link>
              }
            />

            <UiCard padding={false} className="overflow-hidden">
              <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
                <div className="grid gap-6 p-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
                  {statsStrip.map((stat, index) => (
                    <motion.div
                      key={stat.title}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.05 }}
                      whileHover={{ y: -2 }}
                      className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
                    >
                      <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                        />
                        <div className="relative flex gap-3">
                          <div
                            className={clsx(
                              'relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                              stat.iconWrap
                            )}
                          >
                            <stat.icon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              {stat.title}
                            </p>
                            <p className="mt-0.5 text-xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white sm:text-2xl">
                              {stat.value}
                            </p>
                            <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                              {stat.hint}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="border-b border-slate-200 bg-white/40 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/35">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="relative w-full max-w-md">
                    <label htmlFor="payment-search" className="sr-only">
                      Rechercher un paiement
                    </label>
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      id="payment-search"
                      type="search"
                      autoComplete="off"
                      placeholder="Référence, étudiant, cours…"
                      value={searchTerm}
                      onChange={handleSearch}
                      className={clsx(
                        'w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500',
                        searchTerm.trim() ? 'pr-10' : 'pr-4'
                      )}
                    />
                    {searchTerm.trim() !== '' ? (
                      <button
                        type="button"
                        aria-label="Effacer la recherche"
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(1);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('all');
                        setCurrentPage(1);
                      }}
                      className={clsx(
                        'inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                        activeTab === 'all'
                          ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                      )}
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      <span>Tous</span>
                      <span
                        className={clsx(
                          'ml-0.5 min-w-[1.75rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums',
                          activeTab === 'all'
                            ? 'bg-white/20 text-white'
                            : 'bg-white/70 text-slate-800 dark:bg-slate-950 dark:text-slate-200'
                        )}
                      >
                        {payments.length}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('paid');
                        setCurrentPage(1);
                      }}
                      className={clsx(
                        'inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                        activeTab === 'paid'
                          ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                      )}
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      <span>Payés</span>
                      <span
                        className={clsx(
                          'ml-0.5 min-w-[1.75rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums',
                          activeTab === 'paid'
                            ? 'bg-white/20 text-white'
                            : 'bg-white/70 text-slate-800 dark:bg-slate-950 dark:text-slate-200'
                        )}
                      >
                        {payments.filter((p) => p.isPaid).length}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('partial');
                        setCurrentPage(1);
                      }}
                      className={clsx(
                        'inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                        activeTab === 'partial'
                          ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                      )}
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      <span>Partiels</span>
                      <span
                        className={clsx(
                          'ml-0.5 min-w-[1.75rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums',
                          activeTab === 'partial'
                            ? 'bg-white/20 text-white'
                            : 'bg-white/70 text-slate-800 dark:bg-slate-950 dark:text-slate-200'
                        )}
                      >
                        {payments.filter((p) => !p.isPaid).length}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {(searchTerm.trim() !== '' || activeTab !== 'all') && (
                <div className="flex flex-wrap items-center gap-2 border-b border-forma-200/60 bg-forma-50/50 px-5 py-2.5 dark:border-forma-900/35 dark:bg-forma-950/25">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-forma-800 dark:text-forma-400">
                    Filtres actifs
                  </span>
                  {searchTerm.trim() !== '' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      « {searchTerm.trim()} »
                    </span>
                  ) : null}
                  {activeTab !== 'all' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      {activeTab === 'paid' ? 'Payés uniquement' : 'Partiels uniquement'}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setActiveTab('all');
                      setCurrentPage(1);
                    }}
                    className="ml-auto rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Réinitialiser
                  </button>
                </div>
              )}
            </UiCard>

            <UiCard padding={false} className="overflow-hidden">
              {selectedPayments.length > 0 ? (
                <div className="border-b border-forma-200/60 bg-forma-50/60 px-5 py-4 dark:border-forma-900/40 dark:bg-forma-950/30">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {selectedPayments.length} paiement{selectedPayments.length > 1 ? 's' : ''} sélectionné
                      {selectedPayments.length > 1 ? 's' : ''}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPayments([]);
                          setIsSelectAll(false);
                        }}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                      >
                        Tout désélectionner
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBulkDeleteModal(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Supprimer ({selectedPayments.length})
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-col gap-1 border-b border-slate-200 bg-white/50 px-5 py-3 dark:border-slate-700 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Liste des paiements</p>
                <p className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{pageSliceLabel}</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                  <thead className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur dark:bg-slate-800/95">
                    <tr>
                      <th className="w-12 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={isSelectAll}
                          onChange={handleSelectAll}
                          className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Référence
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Étudiant
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Cours
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Montant
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Statut
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {paginatedPayments.map((payment, rowIdx) => (
                      <tr
                        key={payment.id}
                        className={clsx(
                          'transition-colors',
                          rowIdx % 2 === 0 ? 'bg-white/90 dark:bg-slate-950/35' : 'bg-slate-50/80 dark:bg-slate-900/45',
                          'hover:bg-forma-50/80 dark:hover:bg-forma-950/25'
                        )}
                      >
                        <td className="whitespace-nowrap px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedPayments.includes(payment.id)}
                            onChange={() => handleSelectPayment(payment.id)}
                            className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                          />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center">
                            <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-forma-500/15">
                              <DocumentTextIcon className="h-4 w-4 text-forma-700 dark:text-forma-300" />
                            </div>
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                              {payment.invoiceNumber || payment.reference}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                            {getStudentName(payment)}
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-400">
                            {payment.studentId || payment.memberId || payment.member?.memberCode || payment.student?.code || 'N/A'}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-sm text-slate-900 dark:text-slate-50">
                            {payment.description || payment.course?.title || 'Cours non spécifié'}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-sm text-slate-900 dark:text-slate-50">
                            {new Date(payment.paymentDate || payment.date).toLocaleDateString('fr-FR')}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-sm text-slate-900 dark:text-slate-50">
                            {(parseFloat(payment.amount) || parseFloat(payment.paidAmount) || 0).toLocaleString('fr-FR')} /{' '}
                            {(parseFloat(payment.amount) || parseFloat(payment.totalAmount) || 0).toLocaleString('fr-FR')} DA
                          </div>
                          {!payment.isPaid && (
                            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-200/90 dark:bg-slate-800/80">
                              <div
                                className="h-full rounded-full bg-forma-500"
                                style={{
                                  width: `${((parseFloat(payment.paidAmount) || 0) / (parseFloat(payment.totalAmount) || 1)) * 100}%`,
                                }}
                              />
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={`rounded-full px-2 py-1 text-xs ${
                              payment.isPaid || payment.status === 'paid'
                                ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
                                : 'bg-amber-500/15 text-amber-900 dark:text-amber-100'
                            }`}
                          >
                            {payment.isPaid || payment.status === 'paid' ? 'Payé' : 'Partiel'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                          <div className="flex justify-end gap-2">
                            <Link
                              to={`/payments/${payment.id}`}
                              className="rounded-lg bg-slate-100 p-1.5 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              title="Voir"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Link>
                            <Link
                              to={`/payments/edit/${payment.id}`}
                              className="rounded-lg bg-slate-100 p-1.5 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              title="Modifier"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleDirectPDFExport(payment)}
                              className="rounded-lg bg-slate-100 p-1.5 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              title="Exporter PDF"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDirectPrint(payment)}
                              className="rounded-lg bg-slate-100 p-1.5 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              title="Imprimer"
                            >
                              <PrinterIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(payment.id)}
                              className="rounded-lg bg-red-500/10 p-1.5 text-red-600 transition hover:bg-red-500/20 dark:text-red-400"
                              title="Supprimer"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPayments > paymentsPerPage ? (
                <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-slate-600 dark:text-slate-400">{pageSliceLabel}</p>
                  <nav className="flex flex-wrap items-center justify-end gap-2" aria-label="Pagination">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                      Précédent
                    </button>
                    <div className="flex flex-wrap items-center gap-1">
                      {pageNav.map((node) =>
                        node.type === 'ellipsis' ? (
                          <span key={node.key} className="px-2 py-1 text-sm font-medium text-slate-400" aria-hidden>
                            …
                          </span>
                        ) : (
                          <button
                            key={node.page}
                            type="button"
                            onClick={() => setCurrentPage(node.page)}
                            className={clsx(
                              'min-w-[2.25rem] rounded-lg px-2 py-1 text-sm font-semibold transition',
                              currentPage === node.page
                                ? 'bg-forma-600 text-white shadow-sm'
                                : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                            )}
                          >
                            {node.page}
                          </button>
                        )
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Suivant
                      <ChevronRightIcon className="h-4 w-4" aria-hidden />
                    </button>
                  </nav>
                </div>
              ) : null}
            </UiCard>
          </motion.div>
        }
      />
      <Route
        path="/add"
        element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <Link to="/payments" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Retour aux paiements
              </Link>
            </div>
            <PaymentForm students={students} courses={courses} />
          </motion.div>
        }
      />
      <Route
        path="/edit/:id"
        element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <Link to="/payments" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Retour aux paiements
              </Link>
            </div>
            <PaymentEditRoute 
              payments={payments}
              students={students}
              courses={courses}
            />
          </motion.div>
        }
      />
      <Route
        path="/:id"
        element={<PaymentDetailRoute payments={payments} />}
      />
      </Routes>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <TrashIcon className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-2">
                Confirmer la suppression
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Êtes-vous sûr de vouloir supprimer {selectedPayments.length} paiement{selectedPayments.length > 1 ? 's' : ''} ? 
                Cette action est irréversible.
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowBulkDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage; 